import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  BUG_REPORT_URGENCY_OPTIONS,
  createBugReportFormData,
  validateBugReportForm,
} from "./bugReportHelpers";

const formatFileSize = (sizeBytes) => {
  if (!sizeBytes) return "0 KB";
  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
};

function BugReportDialog({
  open,
  onClose,
  instanceUniqueId,
  accessToken,
  serverUrl,
}) {
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState("minor");
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successTicket, setSuccessTicket] = useState(null);

  const fileAccept = ".pdf,.doc,.docx,.png,.jpeg,.jpg";
  const canClose = !submitting;

  const resetForm = () => {
    setSummary("");
    setDescription("");
    setUrgency("minor");
    setFiles([]);
    setErrors({});
    setServerError("");
    setSuccessTicket(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (!canClose) return;
    onClose?.();
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    setFiles((previous) => [...previous, ...selectedFiles]);
    event.target.value = "";
  };

  const handleRemoveFile = (targetIndex) => {
    setFiles((previous) => previous.filter((_file, index) => index !== targetIndex));
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const nextErrors = validateBugReportForm({ summary, description });
    setErrors(nextErrors);
    setServerError("");

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      setSubmitting(true);

      const formData = createBugReportFormData({
        instanceUniqueId,
        summary,
        description,
        urgency,
        files,
      });

      const response = await axios.post(
        `${serverUrl}/auth/report-bug`,
        formData,
        {
          headers: {
            accessToken,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSuccessTicket(response?.data?.ticket || null);
      setSummary("");
      setDescription("");
      setUrgency("minor");
      setFiles([]);
      setErrors({});
      setServerError("");
    } catch (error) {
      setServerError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Could not submit bug report. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const successMessage = useMemo(() => {
    if (!successTicket?.id) return null;
    return `Ticket #${successTicket.id} has been created and sent to the Elite Command Centre.`;
  }, [successTicket]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 20,
          pb: 1,
        }}
      >
        Report a bug
      </DialogTitle>

      <DialogContent dividers>
        {successTicket ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Alert severity="success">
              <strong>Report submitted successfully.</strong>
              <br />
              {successMessage}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              You can close this window or report another issue.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Typography
              sx={{
                fontSize: 14,
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              Explain what happened, what you expected, and steps we can use to reproduce the issue.
            </Typography>

            {serverError ? <Alert severity="error">{serverError}</Alert> : null}

            <TextField
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              label="Short summary of the problem"
              placeholder="Example: PDF upload fails on order files page"
              fullWidth
              required
              error={Boolean(errors.summary)}
              helperText={errors.summary}
            />

            <TextField
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              label="Description"
              placeholder="Explain what happened, what you expected, and steps we can use to reproduce the issue."
              multiline
              rows={7}
              fullWidth
              required
              error={Boolean(errors.description)}
              helperText={errors.description}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  alignItems: "flex-start",
                  backgroundColor: "#fafafa",
                },
              }}
            />

            <TextField
              select
              label="Urgency"
              value={urgency}
              onChange={(event) => setUrgency(event.target.value)}
              fullWidth
            >
              {BUG_REPORT_URGENCY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Button variant="outlined" component="label" disabled={submitting}>
                Attach screenshots or files
                <input
                  hidden
                  type="file"
                  multiple
                  accept={fileAccept}
                  onChange={handleFileChange}
                />
              </Button>

              {files.length > 0 ? (
                <Box sx={{ mt: 2, display: "grid", gap: 1 }}>
                  {files.map((file, index) => (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        p: 1.25,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                      <Button
                        color="inherit"
                        onClick={() => handleRemoveFile(index)}
                        disabled={submitting}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {successTicket ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="inherit"
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              Close
            </Button>
            <Button
              onClick={resetForm}
              variant="contained"
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 2 }}
            >
              Report another issue
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={!canClose}
              variant="outlined"
              color="inherit"
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 2,
                background: "linear-gradient(135deg, #d30c2b 0%, #a30b24 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #bb0f2a 0%, #8f0d21 100%)",
                },
              }}
            >
              {submitting ? "Submitting..." : "Send Report"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BugReportDialog;
